import {
    AdditionalDocument,
    Document,
    Field,
    FieldMode,
    FieldTypes,
    RecipientType,
    PdfDimensions,
    RecipientSettings,
} from '@/types/types';
import { isNonEmptyString } from '@/utils/text';
import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useAuth } from './Auth';
import { DATE_FORMAT } from '@/constants';

export interface RequestContextValue {
    document: Document | null;
    setDocument: Dispatch<SetStateAction<Document | null>>;
    signInOrder: boolean;
    setSignInOrder: Dispatch<SetStateAction<boolean>>;
    templateId: string;
    setTemplateId: Dispatch<SetStateAction<string>>;
    recipients: string[];
    setRecipients: Dispatch<SetStateAction<string[]>>;
    recipientsTypes: RecipientType[];
    setRecipientsTypes: Dispatch<SetStateAction<RecipientType[]>>;
    recipientsNames: string[];
    setRecipientsNames: Dispatch<SetStateAction<string[]>>;
    recipientsSettings: RecipientSettings[];
    setRecipientsSettings: Dispatch<SetStateAction<RecipientSettings[]>>;
    handleRecipientTypeChange: (index: number, value: RecipientType) => void;
    addRecipient: (type: RecipientType) => void;
    emailMessage: string;
    setEmailMessage: Dispatch<SetStateAction<string>>;
    emailSubject: string;
    setEmailSubject: Dispatch<SetStateAction<string>>;
    pdfDimensions: PdfDimensions;
    setPdfDimensions: Dispatch<SetStateAction<PdfDimensions>>;
    fields: Field[];
    setFields: Dispatch<SetStateAction<Field[]>>;
    addField: (field: Partial<Field>) => void;
    updateField: (newData: Partial<Field>) => void;
    selectField: (id: number | string) => void;
    unSelectAll: () => void;
    removeRecipient: (index: number) => void;
    removeField: (id: number | string) => void;
    removeSelectedField: () => void;
    assignRecipientToField: (id: number | string, recipient: string) => void;
    selectedField: Field | undefined;
    reset: (options?: { defaultRecipientType?: RecipientType }) => void;
    nonEmptyRecipients: string[];
    resetFields: () => void;
    resetFile: () => void;
    setTags: Dispatch<SetStateAction<string[]>>;
    tags: string[];
    additionalDocuments: AdditionalDocument[];
    setAdditionalDocuments: Dispatch<SetStateAction<AdditionalDocument[]>>;
    // Non-PRO plans merge extra files into the main document
    mergedFileNames: string[];
    setMergedFileNames: Dispatch<SetStateAction<string[]>>;
    removeDocument: (documentIndex: number) => void;
    moveDocument: (fromIndex: number, toIndex: number) => void;
    userSigUrl: string | null;
    setUserSigUrl: Dispatch<SetStateAction<string | null>>;
    setLastFontSize: Dispatch<SetStateAction<number | undefined>>;
    unshiftRecipient: (email: string, type: RecipientType) => void;
    removeRecipientFields: (index: number) => void;
    resetRecipientSettings: (recipientIndex: number) => void;
    resetCurrentUserRecipientSettings: (recipientIndex: number) => void;
    hideFromTeam: boolean;
    setHideFromTeam: Dispatch<SetStateAction<boolean>>;
}

// @ts-expect-error
const RequestContext = React.createContext<RequestContextValue>();

interface ProviderProps {
    children: React.ReactNode;
}

const initialField: Field = {
    id: 0,
    type: FieldTypes.SIGNATURE,
    y: 0,
    x: 0,
    width: 0,
    height: 0,
    pageNum: 1,
    documentIndex: 0,
    isSelected: false,
    recipientIndex: null,
    description: null,
    mode: FieldMode.FIELD,
    signature: null,
};

const defaultRecipientType = 'EMAIL';

export const defaultRecipientSetting: RecipientSettings = Object.freeze({
    oneClickSign: false,
});

export const RequestProvider = ({ children }: ProviderProps) => {
    const [document, setDocument] = useState<Document | null>(null);
    const [additionalDocuments, setAdditionalDocuments] = useState<
        AdditionalDocument[]
    >([]);
    const [mergedFileNames, setMergedFileNames] = useState<string[]>([]);
    const [signInOrder, setSignInOrder] = useState<boolean>(false);
    const [templateId, setTemplateId] = useState<string>('');

    const [recipients, setRecipients] = useState<string[]>([]);
    const [recipientsTypes, setRecipientsTypes] = useState<RecipientType[]>([]);
    const [recipientsNames, setRecipientsNames] = useState<string[]>([]);
    const [recipientsSettings, setRecipientsSettings] = useState<
        RecipientSettings[]
    >([]);

    const [emailMessage, setEmailMessage] = useState('');
    const [emailSubject, setEmailSubject] = useState('');

    const [fields, setFields] = useState<Field[]>([]);
    const fieldIdRef = useRef(0);

    const [tags, setTags] = useState<string[]>([]);

    const [pdfDimensions, setPdfDimensions] = useState<PdfDimensions>({});

    const [userSigUrl, setUserSigUrl] = useState<string | null>(null);

    const { user } = useAuth();

    const [lastFontSize, setLastFontSize] = useState<number>();

    const [hideFromTeam, setHideFromTeam] = useState(false);

    const addRecipient = useCallback(
        (type: RecipientType = defaultRecipientType) => {
            setRecipients((prev) => prev.concat(['']));
            setRecipientsTypes((prev) => prev.concat(type));
            setRecipientsNames((prev) => prev.concat(['']));
            setRecipientsSettings((prev) =>
                prev.concat([{ ...defaultRecipientSetting }])
            );
        },
        []
    );

    const resetRecipientSettings = (recipientIndex: number) => {
        setRecipientsSettings((prev) => {
            prev[recipientIndex] = { ...defaultRecipientSetting };
            return [...prev];
        });
    };

    const resetCurrentUserRecipientSettings = (recipientIndex: number) => {
        setRecipientsSettings((prev) => {
            const { otpVerification } = prev[recipientIndex] || {};
            prev[recipientIndex] = {
                ...defaultRecipientSetting,
                otpVerification,
            };
            return [...prev];
        });
    };

    const removeField = useCallback((id: number | string) => {
        setFields((prev) => {
            const copy = [...prev];
            const fieldIndex = copy.findIndex((field) => field.id === id);
            copy.splice(fieldIndex, 1);

            return copy;
        });
    }, []);

    const removeSelectedField = () => {
        const id = fields.find((f) => f.isSelected === true)?.id;

        if (typeof id === 'number' || typeof id === 'string') {
            removeField(id);
        }
    };

    const assignRecipientToField = (id: number | string, recipient: string) => {
        updateField({ id, recipientIndex: recipients.indexOf(recipient) });
    };

    const addField = useCallback(
        (field: Partial<Field>) => {
            setFields((prev) => {
                const copy = [...prev];
                copy.push({
                    ...initialField,
                    ...field,
                    id: fieldIdRef.current,
                    ...(lastFontSize &&
                        field.type === FieldTypes.TEXT && {
                            height: lastFontSize,
                        }),
                    ...(field.type === FieldTypes.DATE && {
                        format: field.format || DATE_FORMAT,
                    }),
                    // width: initialFieldSize[field.type!].width,
                    // height: initialFieldSize[field.type!].height,
                });
                fieldIdRef.current++;
                return copy;
            });
        },
        [lastFontSize]
    );

    const updateField = useCallback(
        ({ id, ...newFieldData }: Partial<Field>) => {
            setFields((prev) => {
                const copy = [...prev];
                const fieldIndex = copy.findIndex((f) => f.id === id);
                copy[fieldIndex] = { ...copy[fieldIndex], ...newFieldData };
                return copy;
            });
        },
        []
    );

    const unshiftRecipient = useCallback(
        (recipient: string, type: RecipientType) => {
            setRecipients((prev) => {
                const copy = [...prev];
                copy.unshift(recipient);
                return copy;
            });

            setRecipientsTypes((prev) => {
                const copy = [...prev];
                copy.unshift(type);
                return copy;
            });

            setRecipientsNames((prev) => {
                const copy = [...prev];
                copy.unshift('');
                return copy;
            });

            setRecipientsSettings((prev) => {
                const copy = [...prev];
                copy.unshift({ ...defaultRecipientSetting });
                return copy;
            });

            fields.forEach(({ id, recipientIndex }) => {
                if (typeof recipientIndex !== 'number') return;

                updateField({ id, recipientIndex: recipientIndex + 1 });
            });
        },
        [fields, updateField]
    );

    const removeRecipientFields = useCallback(
        (index: number) => {
            const ids = fields
                .filter((f) => f.recipientIndex === index)
                .map((f) => f.id);

            ids.forEach((id) => removeField(id));
        },
        [fields, removeField]
    );

    const removeRecipient = useCallback(
        (index: number) => {
            const removeAtIndex = (prev: any) => {
                const copy = [...prev];
                copy.splice(index, 1);
                return copy;
            };

            setRecipients(removeAtIndex);
            setRecipientsTypes(removeAtIndex);
            setRecipientsNames(removeAtIndex);
            setRecipientsSettings(removeAtIndex);

            fields.forEach(({ id, recipientIndex }) => {
                if (typeof recipientIndex !== 'number') return;

                if (recipientIndex === index) {
                    removeField(id);
                }

                if (recipientIndex > index) {
                    updateField({ id, recipientIndex: recipientIndex - 1 });
                }
            });
        },
        [fields, removeField, updateField]
    );

    const unSelectAll = useCallback(() => {
        setFields((prev) => {
            const copy = [...prev];

            copy.forEach((field) => (field.isSelected = false));
            return copy;
        });
    }, []);

    const selectField = (id: number | string) => {
        // unSelectAll();
        // updateField({ id, isSelected: true });

        setFields(
            fields.map((field) => ({
                ...field,
                isSelected: field.id === id ? true : false,
            }))
        );
    };

    const handleRecipientTypeChange = useCallback(
        (index: number, value: RecipientType) => {
            setRecipientsTypes((prev) => {
                const copy = [...prev];
                copy[index] = value;
                return copy;
            });

            if (value === 'EMAIL') {
                setRecipientsNames((prev) => {
                    const copy = [...prev];
                    copy[index] = '';
                    return copy;
                });
            }
        },
        []
    );

    const resetFields = () => {
        setFields([]);
    };

    const resetFile = () => {
        setDocument(null);
        setAdditionalDocuments([]);
        setMergedFileNames([]);
    };

    // Removing the main document (index 0) resets all documents and fields
    const removeDocument = useCallback((documentIndex: number) => {
        if (documentIndex === 0) {
            setDocument(null);
            setAdditionalDocuments([]);
            setMergedFileNames([]);
            setFields([]);
            setPdfDimensions({});
            return;
        }

        setAdditionalDocuments((prev) =>
            prev.filter((_, i) => i !== documentIndex - 1)
        );

        setFields((prev) =>
            prev
                .filter((f) => (f.documentIndex ?? 0) !== documentIndex)
                .map((f) =>
                    (f.documentIndex ?? 0) > documentIndex
                        ? { ...f, documentIndex: f.documentIndex! - 1 }
                        : f
                )
        );

        setPdfDimensions((prev) =>
            Object.fromEntries(
                Object.entries(prev).flatMap(([key, value]) => {
                    const [docIndex, pageNum] = key.split(':').map(Number);
                    if (docIndex === documentIndex) return [];
                    const newDocIndex =
                        docIndex > documentIndex ? docIndex - 1 : docIndex;
                    return [[`${newDocIndex}:${pageNum}`, value]];
                })
            )
        );
    }, []);

    // Reorders extra documents (indexes >= 1); the main document can't move
    const moveDocument = useCallback((fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex || fromIndex < 1 || toIndex < 1) return;

        const newIndexOf = (index: number) => {
            if (index === fromIndex) return toIndex;
            if (fromIndex < toIndex && index > fromIndex && index <= toIndex)
                return index - 1;
            if (fromIndex > toIndex && index >= toIndex && index < fromIndex)
                return index + 1;
            return index;
        };

        setAdditionalDocuments((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex - 1, 1);
            next.splice(toIndex - 1, 0, moved);
            return next;
        });

        setFields((prev) =>
            prev.map((f) => {
                const index = f.documentIndex ?? 0;
                const newIndex = newIndexOf(index);
                return newIndex === index ? f : { ...f, documentIndex: newIndex };
            })
        );

        setPdfDimensions((prev) =>
            Object.fromEntries(
                Object.entries(prev).map(([key, value]) => {
                    const [docIndex, pageNum] = key.split(':').map(Number);
                    return [`${newIndexOf(docIndex)}:${pageNum}`, value];
                })
            )
        );
    }, []);

    const resetRecipients = useCallback(() => {
        setRecipients([]);
        setRecipientsTypes([]);
        setRecipientsNames([]);
        setRecipientsSettings([]);
    }, []);

    const resetRecipientsToDefault = useCallback(
        (defaultRecipientType: RecipientType) => {
            setRecipients(['']);
            setRecipientsTypes([defaultRecipientType]);
            setRecipientsNames(['']);
            setRecipientsSettings([defaultRecipientSetting]);
        },
        []
    );

    const reset = useCallback(
        ({
            defaultRecipientType,
        }: { defaultRecipientType?: RecipientType } = {}) => {
            resetFile();
            resetFields();
            setSignInOrder(false);
            setEmailMessage('');
            setEmailSubject('');
            setPdfDimensions({});
            setUserSigUrl(null);
            setTags([]);
            setAdditionalDocuments([]);
            setTemplateId('');
            setLastFontSize(undefined);
            setHideFromTeam(false);

            if (defaultRecipientType) {
                resetRecipientsToDefault(defaultRecipientType);
            } else {
                resetRecipients();
            }
        },
        [resetRecipients, resetRecipientsToDefault]
    );

    useEffect(() => {
        if (!user) {
            reset();
        }
    }, [reset, user]);

    useEffect(() => {
        if (
            recipients.length !== recipientsNames.length ||
            recipientsNames.length !== recipientsTypes.length
        ) {
            resetRecipients();
        }
    }, [
        recipients.length,
        recipientsNames.length,
        recipientsTypes.length,
        resetRecipients,
    ]);

    const selectedField = fields.find((field) => field.isSelected);

    const nonEmptyRecipients = recipients.filter((r) => isNonEmptyString(r));

    const value = {
        document,
        setDocument,
        signInOrder,
        setSignInOrder,
        recipients,
        setRecipients,
        emailMessage,
        setEmailMessage,
        emailSubject,
        setEmailSubject,
        fields,
        setFields,
        addField,
        updateField,
        selectField,
        unSelectAll,
        removeRecipient,
        removeField,
        removeSelectedField,
        assignRecipientToField,
        selectedField,
        reset,
        nonEmptyRecipients,
        pdfDimensions,
        setPdfDimensions,
        resetFields,
        resetFile,
        setTags,
        tags,
        userSigUrl,
        setUserSigUrl,
        unshiftRecipient,
        removeRecipientFields,
        recipientsTypes,
        handleRecipientTypeChange,
        addRecipient,
        setRecipientsTypes,
        recipientsNames,
        setRecipientsNames,
        recipientsSettings,
        setRecipientsSettings,
        additionalDocuments,
        setAdditionalDocuments,
        mergedFileNames,
        setMergedFileNames,
        removeDocument,
        moveDocument,
        templateId,
        setTemplateId,
        setLastFontSize,
        resetRecipientSettings,
        resetCurrentUserRecipientSettings,
        hideFromTeam,
        setHideFromTeam,
    };

    return (
        <RequestContext.Provider value={value}>
            {children}
        </RequestContext.Provider>
    );
};

export const useRequestContext = () => {
    const context = React.useContext(RequestContext);
    if (context === undefined) {
        throw new Error(
            'useRequestContext must be used within a RequestProvider'
        );
    }
    return context;
};
